import {
  Badge,
  Button,
  DatePickerFieldController,
  SelectFieldController,
  Stack,
  ViewItemField,
  ViewItemFieldWithChildren,
} from '@fukuri-admins/ui/common';
import type { FormEventHandler } from 'react';
import styles from './styles.module.scss';

type Props = {
  /** 利用企業名 */
  clientName: string;
  /** 契約プラン名 */
  contractPlanName: string;
  /** 契約プラン種別バッジ */
  kindBadge: {
    /** 表示ラベル */
    label: string;
    /** バッジステータス */
    status: 'neutral' | 'info';
  };
  /** 紐づくギフトプラン（フォーマット済み） */
  giftPlan: string;
  /** 顧客請求単価（フォーマット済み） */
  customerChargeUnitPrice: string;
  /** 税率（フォーマット済み） */
  taxRatePercent: string;
  /** 契約プランの選択肢 */
  contractPlanOptions: { value: string; label: string }[];
  /** 開始日フィールドを無効化するか */
  isStartedOnDisabled: boolean;
  /** 契約プランセレクトを無効化するか */
  isContractPlanDisabled: boolean;
  /** 終了日の選択可能開始日 */
  endedOnFromDate: Date;
  /** 保存ボタンを無効化するか */
  isButtonDisabled: boolean;
  /** フォーム送信ハンドラ */
  onSubmit: FormEventHandler<HTMLFormElement>;
  /** キャンセルボタンハンドラ */
  onCancel: React.ComponentProps<typeof Button>['onClick'];
};

/**
 * 企業契約期間 編集フォーム Presentation コンポーネント
 *
 * - 編集不可項目（読み取り表示）: 利用企業 / 契約プラン情報（プラン名 / 種別 / ギフトプラン / 顧客請求単価 / 税率）
 * - 編集可能項目: 開始日 / 終了日 / 契約プラン（disabled 制御あり）
 * - パートナー販売手数料・Leafea収益単価は PartnerAdmin 用 API レスポンスに含まれないため表示しない
 * - 開始日・契約プランは is_started_on_editable / is_contract_plan_editable に応じて disabled になる
 * - 終了日は ended_on_min から選択可能
 */
export const EditClientContractTermPresentation: React.FC<Props> = ({
  clientName,
  contractPlanName,
  kindBadge,
  giftPlan,
  customerChargeUnitPrice,
  taxRatePercent,
  contractPlanOptions,
  isStartedOnDisabled,
  isContractPlanDisabled,
  endedOnFromDate,
  isButtonDisabled,
  onSubmit,
  onCancel,
}) => (
  <form onSubmit={onSubmit}>
    <Stack direction='column' gap={24} width='466px'>
      <Stack direction='column' gap={16}>
        <ViewItemField label='利用企業' direction='row' value={clientName} />
        <ViewItemField label='契約プラン' direction='row' value={contractPlanName} />
        <ViewItemFieldWithChildren label='種別' direction='row'>
          <Badge status={kindBadge.status} variant='outline' size='sm' label={kindBadge.label} />
        </ViewItemFieldWithChildren>
        <ViewItemField label='紐づくギフトプラン' direction='row' value={giftPlan} />
        <ViewItemField label='顧客請求単価' direction='row' value={customerChargeUnitPrice} />
        <ViewItemField label='税率' direction='row' value={taxRatePercent} />
      </Stack>
      <Stack direction='column' gap={16}>
        <SelectFieldController
          name='contractPlanId'
          fieldWrapperProps={{ labelText: '契約プラン' }}
          select={{
            placeholder: '契約プランを選択してください',
            options: contractPlanOptions,
            disabled: isContractPlanDisabled,
          }}
        />
        <Stack direction='row' gap={16} alignItems='flex-start'>
          <div className={styles.dateFieldWrapper}>
            <DatePickerFieldController
              name='startedOn'
              fieldWrapperProps={{ labelType: 'required', labelText: '開始日' }}
              datePicker={{ disabled: isStartedOnDisabled }}
            />
          </div>
          <div className={styles.dateFieldWrapper}>
            <DatePickerFieldController
              name='endedOn'
              fieldWrapperProps={{ labelText: '終了日' }}
              datePicker={{ fromDate: endedOnFromDate }}
            />
          </div>
        </Stack>
      </Stack>
      <Stack direction='row' gap={8}>
        <Button type='button' variant='outline' size='md' onClick={onCancel}>
          キャンセル
        </Button>
        <Button type='submit' size='md' disabled={isButtonDisabled}>
          保存
        </Button>
      </Stack>
    </Stack>
  </form>
);
